import PageToolbar from '@/components/linear/page-toolbar';
import { invoiceDeliveryPath, invoiceVoidancePath, invoicesPath } from '@/lib/linear-paths';
import {
  formatUsdFromCents,
  invoiceLabel,
  invoiceStatusLabel,
  type Invoice,
  type InvoiceLine,
} from '@/types/invoice';
import { Form, Link } from '@inertiajs/react';
import { useState } from 'react';

interface Props {
  invoice: Invoice;
}

export default function Show({ invoice }: Props) {
  const status = (invoice.status || '').toLowerCase();
  const canSend = status === 'draft' || status === 'open';
  const canVoid = status === 'open';
  const lines = invoice.lines ?? [];
  const [acting, setActing] = useState(false);

  return (
    <div className="ln-page">
      <PageToolbar
        title={invoiceLabel(invoice)}
        showNew={false}
        actions={
          <>
            <Link href={invoicesPath()} className="ln-toolbar-btn">
              All invoices
            </Link>
            {invoice.hostedInvoiceUrl ? (
              <a
                href={invoice.hostedInvoiceUrl}
                target="_blank"
                rel="noreferrer"
                className="ln-toolbar-btn"
              >
                Payment page
              </a>
            ) : null}
            {canSend ? (
              <Form
                method="post"
                action={invoiceDeliveryPath(invoice.id)}
                style={{ display: 'inline-flex' }}
                onStart={() => setActing(true)}
                onFinish={() => setActing(false)}
              >
                {({ processing }) => (
                  <button
                    type="submit"
                    className="ln-primary-btn"
                    disabled={processing || acting}
                    style={{ marginLeft: 0, opacity: processing || acting ? 0.5 : 1 }}
                  >
                    Send
                  </button>
                )}
              </Form>
            ) : null}
            {canVoid ? (
              <Form
                method="post"
                action={invoiceVoidancePath(invoice.id)}
                style={{ display: 'inline-flex' }}
                onStart={() => setActing(true)}
                onFinish={() => setActing(false)}
              >
                {({ processing }) => (
                  <button
                    type="submit"
                    className="ln-toolbar-btn"
                    disabled={processing || acting}
                    style={{ opacity: processing || acting ? 0.5 : 1 }}
                  >
                    Void
                  </button>
                )}
              </Form>
            ) : null}
          </>
        }
      />
      <div className="ln-page-scroll" style={{ padding: '24px 32px 64px', maxWidth: 760 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20 }}>
          <h1 style={{ fontSize: '1.125rem', fontWeight: 590 }}>{invoiceLabel(invoice)}</h1>
          <span className="ln-label">{invoiceStatusLabel(invoice.status)}</span>
        </div>

        <div className="ln-property">
          <div className="ln-property-label">Customer</div>
          <div>
            <div>{invoice.customer?.name || 'No customer'}</div>
            {invoice.customer?.email ? (
              <div className="ln-inbox-sub">{invoice.customer.email}</div>
            ) : null}
          </div>
        </div>
        <div className="ln-property">
          <div className="ln-property-label">Due</div>
          <div>{formatDisplayDate(invoice.dueOn)}</div>
        </div>
        <div className="ln-property">
          <div className="ln-property-label">Sent</div>
          <div>{formatDisplayDate(invoice.sentAt)}</div>
        </div>
        <div className="ln-property" style={{ alignItems: 'flex-start' }}>
          <div className="ln-property-label">Memo</div>
          <div style={{ whiteSpace: 'pre-wrap' }}>{invoice.memo?.trim() ? invoice.memo : '—'}</div>
        </div>

        <table
          style={{
            width: '100%',
            borderCollapse: 'collapse',
            marginTop: 28,
            fontSize: '0.8125rem',
          }}
        >
          <thead>
            <tr style={{ color: 'var(--ln-muted)', textAlign: 'left' }}>
              <th style={headerCell}>Description</th>
              <th style={{ ...headerCell, textAlign: 'right' }}>Qty</th>
              <th style={{ ...headerCell, textAlign: 'right' }}>Unit price</th>
              <th style={{ ...headerCell, textAlign: 'right' }}>Amount</th>
            </tr>
          </thead>
          <tbody>
            {lines.map((line, index) => (
              <tr
                key={line.id ?? `${line.description}-${index}`}
                style={{ borderTop: '1px solid var(--ln-border)' }}
              >
                <td style={bodyCell}>{line.description}</td>
                <td style={{ ...bodyCell, textAlign: 'right' }}>{formatQuantity(line.quantity)}</td>
                <td style={{ ...bodyCell, textAlign: 'right' }}>
                  {formatUsdFromCents(line.unitAmountCents)}
                </td>
                <td style={{ ...bodyCell, textAlign: 'right' }}>
                  {formatUsdFromCents(lineAmountCents(line))}
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        <div
          style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 16, fontWeight: 590 }}
        >
          Total {formatUsdFromCents(invoice.totalCents)}
        </div>
      </div>
    </div>
  );
}

const headerCell = {
  fontWeight: 510,
  padding: '8px 0',
} as const;

const bodyCell = {
  padding: '10px 0',
  verticalAlign: 'top' as const,
};

function lineAmountCents(line: InvoiceLine): number {
  const quantity = Number(line.quantity);
  if (!Number.isFinite(quantity) || !Number.isFinite(line.unitAmountCents)) return 0;

  return Math.round(quantity * line.unitAmountCents);
}

function formatQuantity(quantity: number | string): string {
  const value = Number(quantity);
  if (!Number.isFinite(value)) return String(quantity);

  return new Intl.NumberFormat('en-US', { maximumFractionDigits: 2 }).format(value);
}

function formatDisplayDate(value: string | null): string {
  if (!value) return '—';

  const dateOnly = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  if (dateOnly) {
    const date = new Date(
      Date.UTC(Number(dateOnly[1]), Number(dateOnly[2]) - 1, Number(dateOnly[3]))
    );
    return new Intl.DateTimeFormat('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      timeZone: 'UTC',
    }).format(date);
  }

  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return '—';

  return new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  }).format(parsed);
}
